'use client'

import { GradientDivider } from '@ethena/shared-ui/src/components/GradientDivider'
import { HighlightGradientBox } from '@ethena/shared-ui/src/components/HighlightGradientBox'
import { Pill } from '@ethena/shared-ui/src/components/Pill'
import { DAPP_URL } from '@ethena/shared-ui/src/constants/url'
import Image from 'next/image'
import Link from 'next/link'

import { Fragment } from 'react'

import { SectionHeader } from '@/components/SectionHeader'
import { Stat } from '@/components/Stat'
import { useTransparencyData } from '@/hooks/useTransparencyData'

const IMG_DIMENSIONS: Record<string, undefined | { width?: number; height?: number; className?: string }> = {
  '/ceffu.svg': {
    height: 22,
    width: 90,
  },
  '/chainlink.svg': {
    height: 24,
    width: 115,
  },
  '/chaoslabs.svg': {
    height: 24,
    width: 125,
  },
  '/copper.svg': {
    height: 24,
    width: 120,
  },
  '/h&t.svg': {
    height: 48,
    width: 100,
  },
  '/llamarisk.svg': {
    height: 48,
    width: 90,
  },
}

export const TransparencySection = () => {
  const { backingLastUpdated, protocolBackingRatio } = useTransparencyData()

  const gradientBoxData = [
    {
      cornerImage: '/chart.svg',
      description: 'View real-time information about the allocation of backing assets.',
      link: {
        href: `${DAPP_URL}/dashboards/transparency`,
        text: 'Learn more',
      },
      title: 'Real-Time Backing Assets',
    },
    {
      description: 'Independent third-party proofs of the value of the backing assets & delta neutrality.',
      imageList: ['/h&t.svg', '/chaoslabs.svg', '/llamarisk.svg', '/chainlink.svg'],
      link: {
        href: `${DAPP_URL}/dashboards/transparency`,
        text: 'Learn more',
      },
      title: 'Weekly Proof of Reserves ',
    },
    {
      description: 'Independent attestations of the value of the backing assets residing with custodians.',
      imageList: ['/copper.svg', '/ceffu.svg'],
      link: {
        href: `${DAPP_URL}/dashboards/transparency`,
        text: 'Latest attestation',
      },
      title: 'Monthly Custodian Attestations',
    },
  ]

  const stats = [
    {
      image: '/ratio.svg',
      title: 'Protocol Backing Ratio',
      tooltip: backingLastUpdated,
      value: protocolBackingRatio,
    },
    {
      image: '/compliance.svg',
      title: 'Time below $0.997',
      value: '<0.2%',
    },
    {
      image: '/vault.svg',
      title: '24/7 Mint/Redeem Availability',
      value: '100%',
    },
  ]

  return (
    <section className="landing-section">
      <div className="landing-layout w-full mx-auto flex flex-col items-center justify-center pt-20 hlg:pt-0">
        <div className="mb-10 w-fit xl:hidden xl:hlg:block">
          <Pill>
            <p className="font-inter">Integrity</p>
          </Pill>
        </div>
        <SectionHeader title="Unparalleled Transparency" />
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 justify-center items-stretch gap-8 w-full">
          {gradientBoxData.map(({ cornerImage, description, imageList, link, title }, index) => (
            <HighlightGradientBox
              key={index}
              cornerImage={!!cornerImage}
              className=" lg:flex-1 lg:first:col-span-2 xl:first:col-span-1"
            >
              <div className="flex flex-col gap-12 py-10 px-6 sm:px-8 h-full relative z-10">
                <div className="flex flex-col gap-3 flex-grow">
                  <h2 className="text-text-primary font-medium text-2xl">{title}</h2>
                  <div className="text-text-secondary text-base font-inter">{description}</div>
                  {link && (
                    <Link
                      href={link.href}
                      className="group text-text-accent text-xs uppercase font-medium flex items-center gap-2 tracking-[1.32px]"
                    >
                      {link.text}
                      <Image
                        src="/chevron-right-blue.svg"
                        alt="chevron-right"
                        width={10}
                        height={10}
                        className="h-2 w-2 group-hover:translate-x-1 transition duration-300 ease-in-out"
                      />
                    </Link>
                  )}
                </div>
                {imageList?.length ? (
                  <ul className="list-none flex gap-6 2xl:gap-8 flex-wrap items-center max-w-[300px]">
                    {imageList.map(src => {
                      const { height, width } = IMG_DIMENSIONS[src] ?? {}
                      return (
                        <li key={src}>
                          <Image
                            src={src}
                            width={width ?? 136}
                            height={height ?? 36}
                            className="object-contain"
                            alt={src}
                          />
                        </li>
                      )
                    })}
                  </ul>
                ) : null}
                {cornerImage && (
                  <Image
                    src={cornerImage}
                    alt="corner"
                    width={300}
                    height={300}
                    className="absolute bottom-0 right-0"
                  />
                )}
              </div>
            </HighlightGradientBox>
          ))}
        </div>
        <div className="w-full mx-auto mt-[100px] lg:mt-[20px] lg:hlg:mt-[100px]">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_100px_1fr_100px_1fr] gap-10 lg:gap-0 justify-center items-center w-full">
            {stats.map((stat, index) => (
              <Fragment key={index}>
                <Stat key={stat.title} {...stat} />
                {index < stats.length - 1 && (
                  <div className="hidden lg:flex justify-center">
                    <GradientDivider height="h-[120px]" />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
